/**
 * CDN Runtime Config Loader
 *
 * PURPOSE
 * -------
 * Allows safe, non-secret client UX settings (branding, theme, video feed,
 * ad, language, refresh intervals) to be changed at runtime via a CDN-hosted
 * JSON file — without redeploying the app.
 *
 * DESIGN CONTRACT
 * ---------------
 * - Bundled TypeScript config is ALWAYS the source of truth and fallback.
 * - Runtime override is OPTIONAL. Disabled by default.
 * - A missing, unreachable, invalid, or timed-out CDN URL must NEVER break the app.
 * - Only an explicit allow-list of safe UX fields can be overridden.
 * - API tokens, clientId, secrets, and business logic cannot be overridden.
 * - CDN JSON must not contain arbitrary JavaScript or executable content.
 *
 * URL RESOLUTION
 * --------------
 * 1. bundledConfig.runtimeConfig.url        (explicit override, per-client env var)
 * 2. `${VUE_APP_CDN_BASE_URL}/clients/<clientId>/config/config.json`   (derived; Eber's CDN layout)
 * 3. If neither is set, the fetch is skipped and bundled config is used.
 *
 * The resolved URL is automatically cache-busted with `?v=<VUE_APP_VERSION>`
 * when VUE_APP_VERSION is defined, so a deploy invalidates stale CDN copies.
 *
 * USAGE (main.ts)
 * ---------------
 *   await applyRuntimeConfig(store.state.clientConfig.config, store.commit.bind(store))
 *   // called BEFORE resolveLanguage() and createApp().mount() so all
 *   // downstream init (i18n locale, theme, refresh timers) uses the final config.
 */

import type { ClientConfig, LanguageCode, VideoFitMode } from './types'

// ---------------------------------------------------------------------------
// Validation constants
// ---------------------------------------------------------------------------

const ALLOWED_LANGUAGES: readonly string[] = ['en', 'es']
const ALLOWED_FIT_MODES: readonly string[] = ['contain', 'cover', 'fill']
const MIN_REFRESH_MS = 60_000

// ---------------------------------------------------------------------------
// Type guards
// ---------------------------------------------------------------------------

type Raw = Record<string, unknown>

function isString(v: unknown): v is string {
  return typeof v === 'string'
}

function isPositiveNumber(v: unknown): v is number {
  return typeof v === 'number' && isFinite(v) && v > 0
}

function isBoolean(v: unknown): v is boolean {
  return typeof v === 'boolean'
}

function isStringOrNull(v: unknown): v is string | null {
  return v === null || isString(v)
}

function isObj(v: unknown): v is Raw {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function isLanguageCode(v: unknown): v is LanguageCode {
  return isString(v) && ALLOWED_LANGUAGES.includes(v)
}

function isFitMode(v: unknown): v is VideoFitMode {
  return isString(v) && ALLOWED_FIT_MODES.includes(v)
}

function isRefreshMs(v: unknown): v is number {
  return typeof v === 'number' && isFinite(v) && v >= MIN_REFRESH_MS
}

// ---------------------------------------------------------------------------
// Merge (pure function — safe to unit test without a DOM)
// ---------------------------------------------------------------------------

/**
 * Validates and deep-merges safe override fields from a raw CDN JSON object
 * into a shallow copy of the bundled config.
 *
 * Fields that fail type/range checks are silently ignored — the bundled value
 * is preserved. Fields not in the allow-list (clientId, domain, video.provider,
 * API keys, etc.) are never touched.
 */
export function mergeRuntimeOverride(bundled: ClientConfig, raw: unknown): ClientConfig {
  if (!isObj(raw)) return bundled

  const result: ClientConfig = {
    ...bundled,
    branding: { ...bundled.branding },
    theme: { ...bundled.theme },
    video: { ...bundled.video },
    ad: { ...bundled.ad },
    language: { ...bundled.language },
    features: { ...bundled.features },
    refresh: {
      ...bundled.refresh,
      intervals: { ...bundled.refresh.intervals },
    },
  }

  // top-level --------------------------------------------------------------
  if (isString(raw.displayName)) result.displayName = raw.displayName

  // branding ----------------------------------------------------------------
  if (isObj(raw.branding)) {
    const b = raw.branding
    if (isStringOrNull(b.logoUrl)) result.branding.logoUrl = b.logoUrl
    if (isString(b.logoAlt)) result.branding.logoAlt = b.logoAlt
    if (isStringOrNull(b.backgroundImageUrl))
      result.branding.backgroundImageUrl = b.backgroundImageUrl
    if (isPositiveNumber(b.logoMaxWidth)) result.branding.logoMaxWidth = b.logoMaxWidth
    if (isPositiveNumber(b.logoMaxHeight)) result.branding.logoMaxHeight = b.logoMaxHeight
  }

  // theme -------------------------------------------------------------------
  if (isObj(raw.theme)) {
    const t = raw.theme
    if (isString(t.backgroundColor)) result.theme.backgroundColor = t.backgroundColor
    if (isString(t.primaryColor)) result.theme.primaryColor = t.primaryColor
    if (isString(t.accentColor)) result.theme.accentColor = t.accentColor
    if (isString(t.textColor)) result.theme.textColor = t.textColor
    if (isString(t.surfaceColor)) result.theme.surfaceColor = t.surfaceColor
  }

  // video -------------------------------------------------------------------
  // NOTE: video.provider cannot be overridden — it controls which player module
  // is loaded at build time.
  if (isObj(raw.video)) {
    const v = raw.video
    if (isStringOrNull(v.feedUrl)) result.video.feedUrl = v.feedUrl
    if (isString(v.label)) result.video.label = v.label
    if (isBoolean(v.closedCaptions)) result.video.closedCaptions = v.closedCaptions
    if (isFitMode(v.fitMode)) result.video.fitMode = v.fitMode
    if (isFitMode(v.fullscreenFitMode)) result.video.fullscreenFitMode = v.fullscreenFitMode
  }

  // ad ----------------------------------------------------------------------
  if (isObj(raw.ad)) {
    const a = raw.ad
    if (isBoolean(a.enabled)) result.ad.enabled = a.enabled
    if (isStringOrNull(a.imageUrl)) result.ad.imageUrl = a.imageUrl
    if (isString(a.alt)) result.ad.alt = a.alt
  }

  // language ----------------------------------------------------------------
  // Only values in ALLOWED_LANGUAGES are accepted.
  // Unsupported codes are silently dropped to prevent a blank UI.
  if (isObj(raw.language)) {
    const l = raw.language
    if (isLanguageCode(l.defaultLanguage)) result.language.defaultLanguage = l.defaultLanguage
    if (Array.isArray(l.supportedLanguages)) {
      const langs = (l.supportedLanguages as unknown[]).filter(isLanguageCode)
      if (langs.length > 0) result.language.supportedLanguages = langs
    }
  }

  // features ----------------------------------------------------------------
  if (isObj(raw.features)) {
    const f = raw.features
    if (isBoolean(f.showVideo)) result.features.showVideo = f.showVideo
    if (isBoolean(f.showLogo)) result.features.showLogo = f.showLogo
  }

  // refresh -----------------------------------------------------------------
  // Intervals below MIN_REFRESH_MS (60 s) are rejected to protect TV hardware
  // from accidental aggressive polling.
  if (isObj(raw.refresh)) {
    const r = raw.refresh
    if (isBoolean(r.enabled)) result.refresh.enabled = r.enabled
    if (isObj(r.intervals)) {
      const i = r.intervals
      if (isRefreshMs(i.currentConditionsMs))
        result.refresh.intervals.currentConditionsMs = i.currentConditionsMs
      if (isRefreshMs(i.hourlyForecastMs))
        result.refresh.intervals.hourlyForecastMs = i.hourlyForecastMs
      if (isRefreshMs(i.dailyForecastMs))
        result.refresh.intervals.dailyForecastMs = i.dailyForecastMs
      if (isRefreshMs(i.mapsMs)) result.refresh.intervals.mapsMs = i.mapsMs
    }
  }

  return result
}

// ---------------------------------------------------------------------------
// URL resolution
// ---------------------------------------------------------------------------

/**
 * Resolves the CDN URL to fetch for this client. Returns null if no URL can
 * be determined — caller must skip the fetch.
 *
 * Priority:
 *   1. explicit override (bundledConfig.runtimeConfig.url)
 *   2. derived from VUE_APP_CDN_BASE_URL using clientId
 *
 * SECURITY: only `https://` URLs are accepted in production. `http://` URLs
 * are rejected with a warning so a misconfigured env var can't downgrade the
 * CDN fetch to plaintext. localhost is allowed for dev / Cypress mocks.
 */
export function resolveRuntimeUrl(bundled: ClientConfig): string | null {
  const explicit = bundled.runtimeConfig?.url ?? null
  if (explicit && explicit.length > 0) {
    const safe = enforceHttps(explicit)
    if (!safe) return null
    return appendCacheBuster(safe)
  }

  const base = process.env.VUE_APP_CDN_BASE_URL
  if (base && base.length > 0) {
    const trimmed = base.replace(/\/+$/, '')
    // CDN layout (TVAPP-298, Eber Irigoyen):
    //   {base}/clients/{clientId}/config/config.json
    //   {base}/clients/{clientId}/assets/logo.svg
    //   {base}/clients/{clientId}/assets/background.jpg
    //   {base}/clients/{clientId}/assets/ad.png
    const derived = `${trimmed}/clients/${bundled.clientId}/config/config.json`
    const safe = enforceHttps(derived)
    if (!safe) return null
    return appendCacheBuster(safe)
  }

  return null
}

/**
 * Returns the URL if it's safe to fetch, or null with a console warning if
 * the scheme is plaintext http:// against a non-localhost host. Other schemes
 * (file://, ftp://, etc.) are rejected outright.
 */
function enforceHttps(url: string): string | null {
  try {
    const parsed = new URL(url)
    if (parsed.protocol === 'https:') return url
    if (parsed.protocol === 'http:') {
      const isLocalhost = ['localhost', '127.0.0.1', '0.0.0.0'].includes(parsed.hostname)
      if (isLocalhost) return url
      console.warn(
        `[RuntimeConfig] Rejecting plaintext http:// CDN URL (${url}). Use https:// in production.`,
      )
      return null
    }
    console.warn(`[RuntimeConfig] Rejecting unsupported scheme ${parsed.protocol} (${url}).`)
    return null
  } catch {
    console.warn(`[RuntimeConfig] Could not parse runtime URL: ${url}`)
    return null
  }
}

function appendCacheBuster(url: string): string {
  const version = process.env.VUE_APP_VERSION
  if (!version) return url
  const sep = url.includes('?') ? '&' : '?'
  return `${url}${sep}v=${encodeURIComponent(version)}`
}

// ---------------------------------------------------------------------------
// Fetch helper
// ---------------------------------------------------------------------------

async function fetchWithTimeout(url: string, timeoutMs: number): Promise<unknown> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const res = await fetch(url, { signal: controller.signal })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return await res.json()
  } finally {
    clearTimeout(timer)
  }
}

// ---------------------------------------------------------------------------
// Public entry point
// ---------------------------------------------------------------------------

/**
 * Status object captured during applyRuntimeConfig() — exposed for the debug
 * page so PMs can verify CDN integration without reading source maps.
 */
export interface RuntimeFetchStatus {
  attempted: boolean
  url: string | null
  ok: boolean
  status: number | null
  durationMs: number | null
  error: string | null
}

let lastStatus: RuntimeFetchStatus = {
  attempted: false,
  url: null,
  ok: false,
  status: null,
  durationMs: null,
  error: null,
}

/** Returns a snapshot of the last applyRuntimeConfig() call. */
export function getRuntimeFetchStatus(): RuntimeFetchStatus {
  return { ...lastStatus }
}

/**
 * Attempts to fetch the CDN runtime config and commits the merged result to
 * Vuex via `clientConfig/updateConfig`.
 *
 * Call this in main.ts BEFORE resolveLanguage() and createApp().mount() so
 * that i18n, theme, and refresh timers all see the final merged config from
 * first render.
 *
 * Always resolves — never throws. Failures are logged as warnings only.
 *
 * @param bundledConfig  The config currently in the store (bundled fallback).
 * @param commit         Bound Vuex store.commit for the `clientConfig` module.
 */
export async function applyRuntimeConfig(
  bundledConfig: ClientConfig,
  commit: (type: string, payload: ClientConfig) => void,
): Promise<void> {
  const rt = bundledConfig.runtimeConfig
  if (!rt?.enabled) {
    lastStatus = {
      attempted: false,
      url: null,
      ok: false,
      status: null,
      durationMs: null,
      error: null,
    }
    return
  }

  const url = resolveRuntimeUrl(bundledConfig)
  if (!url) {
    lastStatus = {
      attempted: false,
      url: null,
      ok: false,
      status: null,
      durationMs: null,
      error: 'no url resolved (set VUE_APP_CDN_BASE_URL or per-client URL env)',
    }
    return
  }

  const started = Date.now()
  try {
    const raw = await fetchWithTimeout(url, rt.timeoutMs)
    const merged = mergeRuntimeOverride(bundledConfig, raw)
    commit('clientConfig/updateConfig', merged)
    lastStatus = {
      attempted: true,
      url,
      ok: true,
      status: 200,
      durationMs: Date.now() - started,
      error: null,
    }
    console.info('[RuntimeConfig] CDN override applied from:', url)
  } catch (e) {
    // Never fatal — TV app must always start with bundled config.
    lastStatus = {
      attempted: true,
      url,
      ok: false,
      status: null,
      durationMs: Date.now() - started,
      error: e instanceof Error ? e.message : String(e),
    }
    console.warn('[RuntimeConfig] CDN fetch failed, using bundled config:', e)
  }
}
